import { useEffect, useState } from 'react';

/**
 * How far the bottom of what the user sees is above the bottom of the page's
 * layout viewport, in whole pixels: the on-screen keyboard's height while it
 * is up. A fixed element at the bottom (a bottom sheet) stays put under the
 * iOS keyboard, which only shrinks the visual viewport, so a list at the
 * bottom of the sheet pads itself by this much to scroll its last rows into
 * sight. 0 where the browser has no visualViewport.
 * @returns {number}
 */
const readKeyboardInset = () => {
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  return Math.max(
    0,
    Math.round(window.innerHeight - viewport.height - viewport.offsetTop)
  );
};

/**
 * The on-screen keyboard's height while it is up (see readKeyboardInset),
 * followed as the visual viewport resizes and scrolls.
 * @returns {number}
 */
export function useKeyboardInset() {
  const [inset, setInset] = useState(readKeyboardInset);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const update = () => setInset(readKeyboardInset());
    // What changed between the first render and now.
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return inset;
}

export default useKeyboardInset;
